//#region IMPORTS
import {
  RowData,
  ColumnDef,
  flexRender,
  CellContext,
  useReactTable,
  ExpandedState,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
} from "@tanstack/react-table";
import {
  InstoreTemplate,
  InstoreTemplateTableRow,
} from "../../models/instoreTemplate";
import {
  EyeIcon,
  EyeSlashIcon,
  CodeBracketSquareIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { ADD_PERSO_VARIABLE_MODAL } from "../../models/_consts";
import { SquaresPlusIcon } from "@heroicons/react/20/solid";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import InstoreTemplateDetailTableEditableCell from "./InstoreTemplateDetailTableEditableCell";
import InstoreTemplateDetailTableBody from "./InstoreTemplateDetailTableBody";
import MainTableRowActionDelete from "../_layout/MainTableRowActionDelete";
import MainTableRowActionCreate from "../_layout/MainTableRowActionCreate";
import MainFieldArrayForm from "../_layout/MainFieldArrayForm";
import LoadingSpinner from "../loading/LoadingSpinner";
import classNames from "../../helpers/classNames";
import MainModal from "../_layout/MainModal";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    updateData: (columnId: string, value: unknown, row: TData) => void;
  }
}
//#endregion

//#region INTERFACE
interface Props {
  updateRowCellsHandler: (
    columnId: string,
    value: any,
    row: InstoreTemplateTableRow
  ) => void;
  addParentRowHandler: () => void;
  addChildRowHandler: (themeId: number) => void;
  deleteRowHandler: (row: InstoreTemplateTableRow) => void;
  availableFormats: { id: number; name: string }[];
  instoreTemplate?: InstoreTemplate;
  disabled?: boolean;
  moveRow: (toIndex: number, row: InstoreTemplateTableRow) => void;
  data: InstoreTemplateTableRow[];
}
//#endregion

export default function InstoreTemplateDetailTable({
  updateRowCellsHandler,
  addParentRowHandler,
  addChildRowHandler,
  deleteRowHandler,
  availableFormats,
  instoreTemplate,
  disabled = false,
  moveRow,
  data,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading, lastLoadedTemplateId } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates
  );

  // Local state
  const [selectedCellContext, setSelectedCellContext] =
    useState<CellContext<InstoreTemplateTableRow, any>>();
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const showLoadingSpinner =
    loading &&
    (instoreTemplate ? lastLoadedTemplateId !== instoreTemplate.id : true);
  //#endregion

  //#region LOGIC
  useEffect(() => {
    setExpanded(true);
  }, []);

  const setPersoVariablesHandler = (items: string[]) => {
    if (selectedCellContext) {
      updateRowCellsHandler(
        selectedCellContext?.column.id,
        items,
        selectedCellContext.row.original
      );
    } else {
      toast.warning("No article selected");
    }
    cancelModals();
  };
  //#endregion

  //#region REACT TABLE COLUMNS
  const columns = useMemo<ColumnDef<InstoreTemplateTableRow, any>[]>(() => {
    const addPersoVariablesModalHandler = (
      item: CellContext<InstoreTemplateTableRow, any[]>
    ) => {
      setSelectedCellContext(item);
      dispatch(openModal(ADD_PERSO_VARIABLE_MODAL));
    };

    const columnHelper = createColumnHelper<InstoreTemplateTableRow>();
    const baseColumns: ColumnDef<InstoreTemplateTableRow, any>[] = [
      columnHelper.accessor(({ theme_id }) => theme_id, {
        id: "theme_id",
        size: 250,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.parent ? (
              <button
                {...{
                  disabled,
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: row.getCanExpand() ? "pointer" : "normal" },
                }}
              >
                {row.getIsExpanded() ? (
                  <EyeSlashIcon className="h-4 w-4 ml-2 text-purple-600" />
                ) : (
                  <EyeIcon className="h-4 w-4 ml-2 text-emerald-600" />
                )}
              </button>
            ) : (
              <div className="w-6 h-4" />
            )}

            <div className="w-8 flex justify-center">
              {row.original.parent ? (
                <MainTableRowActionCreate
                  tooltipContent="Add new article to theme"
                  disabled={disabled || !row.getIsExpanded()}
                  tooltipId={String(row.original.theme_id)}
                  createHandler={() =>
                    addChildRowHandler(row.original.theme_id)
                  }
                />
              ) : (
                <div className="w-5 h-5" />
              )}
            </div>

            <span
              className={classNames(row.getCanExpand() ? "font-semibold" : "")}
            >
              {row.original.parent ? "THEME" : "ARTICLE"}
            </span>
          </div>
        ),
        header: ({ table }) => (
          <div className="flex items-center gap-3">
            <button
              {...{
                disabled,
                onClick: table.getToggleAllRowsExpandedHandler(),
              }}
            >
              {table.getIsAllRowsExpanded() ? (
                <EyeSlashIcon className="h-4 w-4 ml-2 text-purple-600" />
              ) : (
                <EyeIcon className="h-4 w-4 ml-2 text-emerald-600" />
              )}
            </button>
            <Fragment>
              <button
                data-tooltip-delay-show={100}
                data-tooltip-content="Add new theme group"
                data-tooltip-place="right"
                data-tooltip-id="ea197269-0a25-4149-ba0c-21760aa2b3b4"
                className={classNames(
                  disabled
                    ? "hover:cursor-not-allowed bg-gray-400"
                    : "hover:cursor-pointer hover:bg-pink-500 bg-pink-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
                  "rounded-full p-2 text-white h-8 w-8 flex items-center justify-center"
                )}
                disabled={disabled}
                onClick={addParentRowHandler}
                type="button"
              >
                <SquaresPlusIcon aria-hidden="true" className="h-4 w-4" />
              </button>
              <Tooltip
                id="ea197269-0a25-4149-ba0c-21760aa2b3b4"
                className="font-bold"
              />
            </Fragment>
            row kind
          </div>
        ),
      }),
      columnHelper.accessor(({ article_name }) => article_name, {
        id: "article_name",
        size: 600,
        cell: (item) =>
          item.row.original.parent ? (
            <span className="ml-3">{item.getValue()}</span>
          ) : (
            <InstoreTemplateDetailTableEditableCell
              {...item}
              disabled={disabled}
            />
          ),
        enableGrouping: false,
        header: () => <span className="ml-3">article</span>,
      }),
      columnHelper.accessor(({ custom_fields }) => custom_fields, {
        id: "custom_fields",
        size: 200,
        cell: (item) =>
          item.row.original.parent ? null : (
            <Fragment>
              <button
                onClick={() => addPersoVariablesModalHandler(item)}
                className="bg-white hover:bg-purple-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 flex items-center gap-x-1.5 rounded-md  px-1 py-1 text-sm font-semibold"
              >
                <CodeBracketSquareIcon
                  className={classNames(
                    item.getValue() && item.getValue().length > 0
                      ? "text-purple-600 hover:text-purple-800"
                      : "text-gray-400 hover:text-purple-600",
                    "w-5 h-5"
                  )}
                />
              </button>
            </Fragment>
          ),
        enableGrouping: false,
        header: () => "perso",
      }),
      // columnHelper.accessor(({ index }) => index, {
      //   id: "index",
      //   size: 100,
      //   cell: (item) => <span className="ml-3">{item.getValue()}</span>,
      //   enableGrouping: false,
      //   header: () => <span className="ml-3">index</span>,
      // }),
      // columnHelper.accessor(({ flatIndex }) => flatIndex, {
      //   id: "flatIndex",
      //   size: 100,
      //   cell: (item) => <span className="ml-3">{item.getValue()}</span>,
      //   enableGrouping: false,
      //   header: () => <span className="ml-3">flatIndex</span>,
      // }),
    ];

    // Dynamic format columns
    const formatColumns: ColumnDef<InstoreTemplateTableRow, any>[] =
      availableFormats.map((availableformat: { id: number; name: string }) => {
        return columnHelper.accessor(
          (row) =>
            row.store_formats_amounts.find(
              (sfa) => sfa.store_format_id === availableformat.id
            )?.amount ?? null,
          {
            enableGrouping: false,
            enableSorting: false,
            size: 200,
            id: `store_format_${availableformat.id}_${availableformat.name}`,
            cell: (item) =>
              item.row.original.parent ? null : (
                <InstoreTemplateDetailTableEditableCell
                  {...item}
                  disabled={disabled}
                />
              ),
            header: () => (
              <span className="font-extrabold text-sky-800">
                {availableformat.name.toUpperCase() ?? "FORMAT"}
              </span>
            ),
          }
        );
      }) ?? [];

    const actionColumns = [
      columnHelper.accessor((row) => row, {
        enableGrouping: false,
        enableSorting: false,
        size: 100,
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="flex justify-center items-center">
              <MainTableRowActionDelete
                tooltipContent="Remove item from list"
                disabled={disabled}
                deleteHandler={() => deleteRowHandler(row.original)}
                tooltipId={String(row.id)}
              />
            </div>
          );
        },
        header: () => null,
      }),
    ];

    return [...baseColumns, ...formatColumns, ...actionColumns];
  }, [
    addParentRowHandler,
    addChildRowHandler,
    deleteRowHandler,
    availableFormats,
    disabled,
    dispatch,
  ]);
  //#endregion

  //#region REACT TABLE INSTANCE
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (originalRow) => originalRow.children,
    getRowId: (row) => row.flatIndex?.toString() || "",
    columns,
    state: { expanded },
    data: data,
    meta: {
      updateData: (
        columnId: string,
        value: any,
        row: InstoreTemplateTableRow
      ) => {
        updateRowCellsHandler(columnId, value, row);
      },
    },
  });
  //#endregion

  //#region UI cleanup
  const cancelModals = () => {
    dispatch(openModal(false));
    setSelectedCellContext(undefined);
  };
  //#endregion

  //#region RENDER
  if (showLoadingSpinner) {
    return <LoadingSpinner />;
  }

  return (
    <Fragment>
      <MainModal type={ADD_PERSO_VARIABLE_MODAL} closeHandler={cancelModals}>
        <MainFieldArrayForm
          onSubmitHandler={(items: string[]) => setPersoVariablesHandler(items)}
          initialItems={
            selectedCellContext?.row.original.custom_fields?.map(
              (value: string) => value
            ) || []
          }
          propertyKey="persoVariableKey"
          fieldName="persoVariables"
          required={false}
          cancel={cancelModals}
        />
      </MainModal>
      <div
        ref={tableContainerRef}
        style={{
          overflow: "auto", //our scrollable table container
          position: "relative", //needed for sticky header
          height: "calc(100vh - 300px)", //scales with viewport height
        }}
      >
        <table
          className={classNames(
            disabled ? "opacity-50 pointer-events-none" : "opacity-100",
            "min-w-full divide-y divide-gray-300"
          )}
        >
          <thead className="sticky top-0 bg-white/80 h-16 z-10 grid">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="flex items-center w-full">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="whitespace-nowrap pr-2 py-3.5 text-left text-xs text-gray-900"
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <InstoreTemplateDetailTableBody
            tableContainerRef={tableContainerRef}
            moveRow={moveRow}
            table={table}
          />
        </table>
      </div>
    </Fragment>
  );
  //#endregion
}
